import React from 'react';
import { X } from 'lucide-react';
import { GeneratedImage } from '@/types';
import { useTranslation, formatDate, getSceneTypeTranslationKey, getLocationTranslationKey, getPeopleTranslationKey } from '@/i18n/useTranslation';

interface MobileInfoDrawerProps {
  isOpen: boolean;
  image: GeneratedImage;
  onClose: () => void;
}

export default function MobileInfoDrawer({ isOpen, image, onClose }: MobileInfoDrawerProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-[100] bg-black/50" onClick={onClose}>
      <div
        className="fixed inset-y-0 right-0 w-full max-w-sm bg-bg-panel shadow-2xl overflow-y-auto pwa-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 h-12 bg-bg-panel border-b border-border flex items-center justify-between px-4 pwa-header">
          <h2 className="text-lg font-semibold">{t('infoPanel.fileInfo')}</h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-bg-control transition-colors flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        <div className="p-4">
          <div className="md:hidden">
            <div className="mb-6">
              <div className="text-[11px] font-semibold text-text-secondary mb-3">
                {t('infoPanel.fileInfo')}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-start">
                  <span className="text-[11px] text-text-secondary">{t('infoPanel.filename')}</span>
                  <span className="text-[11px] text-text-primary text-right max-w-[180px] break-words">
                    {image.filename}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[11px] text-text-secondary">{t('infoPanel.created')}</span>
                  <span className="text-[11px] text-text-primary">{formatDate(image.timestamp)}</span>
                </div>
              </div>
            </div>

            {(image.params?.sceneType || image.params?.location || image.params?.people) &&
              image.params?.imageStyle !== 'infographic' && (
              <div className="mb-6">
                <div className="text-[11px] font-semibold text-text-secondary mb-3">
                  {t('infoPanel.sceneLocation')}
                </div>
                <div className="space-y-2">
                  {image.params.sceneType && (
                    <div className="flex justify-between">
                      <span className="text-[11px] text-text-secondary">{t('infoPanel.sceneType')}</span>
                      <span className="text-[11px] text-text-primary">
                        {t(getSceneTypeTranslationKey(image.params.sceneType))}
                      </span>
                    </div>
                  )}
                  {image.params.location && (
                    <div className="flex justify-between">
                      <span className="text-[11px] text-text-secondary">{t('infoPanel.location')}</span>
                      <span className="text-[11px] text-text-primary">
                        {t(getLocationTranslationKey(image.params.location))}
                      </span>
                    </div>
                  )}
                  {image.params.people && (
                    <div className="flex justify-between">
                      <span className="text-[11px] text-text-secondary">{t('infoPanel.people')}</span>
                      <span className="text-[11px] text-text-primary">
                        {t(getPeopleTranslationKey(image.params.people))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.tags')}</span>
                    <div className="flex flex-wrap gap-1 max-w-[180px] justify-end">
                      {image.params?.images_tags &&
                      Array.isArray(image.params.images_tags) &&
                      image.params.images_tags.length > 0 ? (
                        image.params.images_tags.map((tag: string, idx: number) => (
                          <span
                            key={idx}
                            className="text-[10px] px-1.5 py-0.5 bg-bg-control border border-border rounded text-text-primary"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-[11px] text-text-disabled">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const referencePanelImages = [
                image.params?.productImageUrl
                  ? { src: image.params.productImageUrl, alt: t('label.referenceProductImage') }
                  : null,
                image.params?.referenceImageUrl
                  ? { src: image.params.referenceImageUrl, alt: t('referenceImage.title') }
                  : null,
                image.params?.contentImageUrl
                  ? { src: image.params.contentImageUrl, alt: t('label.contentReference') }
                  : null,
              ].filter((item): item is { src: string; alt: string } => Boolean(item));

              if (referencePanelImages.length === 0) return null;

              const referencePanelTitle =
                referencePanelImages.length === 1
                  ? referencePanelImages[0].alt
                  : t('referenceImage.title');

              return (
                <div className="mb-6">
                  <div className="text-[11px] font-semibold text-text-secondary mb-3">{referencePanelTitle}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {referencePanelImages.map((ref) => (
                      <div key={ref.src} className="rounded overflow-hidden border border-border bg-bg-control aspect-square">
                        <img
                          src={ref.src}
                          alt={ref.alt}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {!(image.params?.sceneType || image.params?.location || image.params?.people) &&
              image.params?.images_tags &&
              Array.isArray(image.params.images_tags) &&
              image.params.images_tags.length > 0 && (
                <div className="mb-6">
                  <div className="text-[11px] font-semibold text-text-secondary mb-3">
                    {t('infoPanel.tags')}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {image.params.images_tags.map((tag: string, idx: number) => (
                      <span
                        key={idx}
                        className="text-[10px] px-1.5 py-0.5 bg-bg-control border border-border rounded text-text-primary"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
